import { css } from "@emotion/react";
import { heicTo } from "heic-to";
import { PDFDocumentProxy } from "pdfjs-dist/types/src/display/api";
import {
  Application,
  Assets,
  Container,
  Graphics,
  Point as PixiPoint,
  Polygon,
  Sprite,
  Text,
  Texture,
} from "pixi.js";
import {
  AmplitudeTrackEvent,
  DebugMessageDetail,
  DebugMessageEvent,
  DocumentLoadedEvent,
  DocumentLoadFailedEvent,
  HighlightNoteEvent,
  ReadyEvent,
  ZoomChangedEvent,
} from "./CustomEvents";
import { fetchAndDetectFileType } from "./fetchAndDetectFileType";
import { loadPDF, renderPage as renderPdfPage } from "./PDFLoader";
import {
  Debugger,
  Document,
  Highlight,
  PanMode,
  Point,
  ZoomConfig,
  ZoomTo,
} from "./types";

// ============================================================================
// Constants
// ============================================================================

export const MIN_ZOOM = 0.1;
export const MAX_ZOOM = 5.0;
const KEYBOARD_PAN_STEP = 40;

const EMPTY_PAN_MODE: PanMode = {
  mode: "pan",
  lastPointerX: 0,
  lastPointerY: 0,
  isDragging: false,
  isPinching: false,
  initialPinchDistance: 0,
  initialScale: 1,
  pinchCenterX: 0,
  pinchCenterY: 0,
};

// ============================================================================
// Main Component
// ============================================================================

export class DocumentViewerElement extends HTMLElement {
  // ============================================================================
  // Properties & State
  // ============================================================================

  /**
   * Private properties
   */
  private document?: Document;
  private highlights?: readonly Highlight[];
  private app: Application;
  private wrapperElement: HTMLElement;
  private observer: ResizeObserver;
  private container: Container;
  private documentEntity: {
    container: Container;
    sprite: Sprite | null;
  };
  private highlightsEntity: {
    container: Container;
    highlights: {
      highlight: Highlight;
      graphics: Graphics;
    }[];
  };
  private mode: PanMode;
  private zoomAnimationTick?: () => void;
  private panDebounceTimer?: number;

  /** Debugger */
  private debugger: Debugger & {
    _highResolutionPdf: Graphics;
    _containers: {
      canvas: Container;
      document: Container;
      container: Container;
    };
  };

  // ============================================================================
  // Lifecycle Methods
  // ============================================================================

  constructor() {
    super();
    this.debugger = {
      canvas: false,
      document: false,
      container: false,
      highResolutionPdf: false,
      _highResolutionPdf: new Graphics(),
      _containers: {
        canvas: new Container(),
        document: new Container(),
        container: new Container(),
      },
    };
    this.app = new Application();
    this.wrapperElement = document.createElement("div");
    this.wrapperElement.className = "container";
    this.wrapperElement.style = "width:100%;height:100%;position:relative";

    const style = document.createElement("style");
    style.textContent = css`
      :host {
        width: 100%;
        height: 100%;
        overflow: hidden;
      }
      canvas {
        outline: none;
      }
    `.styles;
    const shadow = this.attachShadow({ mode: "open" });
    shadow.appendChild(style);
    shadow.appendChild(this.wrapperElement);

    this.observer = new ResizeObserver((events) => {
      const event = events[0];
      const rect = event?.contentRect;
      if (!rect || !this.app.renderer) {
        return;
      }

      this.app.renderer.resize(rect.width, rect.height);
      this.drawCanvasDebugger();
    });

    this.container = new Container();
    this.documentEntity = {
      container: new Container(),
      sprite: null,
    };
    this.highlightsEntity = { container: new Container(), highlights: [] };
    this.mode = EMPTY_PAN_MODE;
    this.highResolutionContainer = new Container();
    this.highResolutionSprite = new Sprite();
  }

  connectedCallback() {
    // Called when the element is added to the DOM
    this.init().then(() => {
      this.initPanMode();
      this.initDebugger();
      this.dispatchEvent(new ReadyEvent());
    });
  }

  disconnectedCallback() {
    // Called when the element is removed from the DOM
    this.destroyPdf();
    this.destroyPanMode();
    this.observer.disconnect();
    this.app.destroy();
    this.destroyDebugger();
  }

  private async init() {
    if (!this.shadowRoot) {
      console.error("Error render, missing shadowRoot");
      return;
    }

    const box = this.wrapperElement.getBoundingClientRect();
    await this.app.init({
      height: box.height,
      width: box.width,
      backgroundAlpha: 0,
    });

    this.observer.observe(this.wrapperElement);
    this.wrapperElement.appendChild(this.app.canvas);

    this.app.canvas.style.position = "absolute";
    this.app.canvas.style.top = "0";
    this.app.canvas.style.left = "0";

    this.app.stage.addChild(this.container);
    this.container.addChild(this.documentEntity.container);
    this.container.addChild(this.highlightsEntity.container);

    this.highResolutionContainer.addChild(this.highResolutionSprite);
    this.highResolutionContainer.addChild(this.debugger._highResolutionPdf);
    this.app.stage.addChild(this.highResolutionContainer);

    if (this.document !== undefined) {
      await this.loadDocument(this.document);

      // Draw initial highlights if any
      if (this.highlights !== undefined && this.highlights.length > 0) {
        this.setHighlights(this.highlights);
      }
    }
  }

  // ============================================================================
  // Document Loading
  // ============================================================================

  public async loadDocument(doc: Document) {
    if (!this.app.renderer) {
      return;
    }

    const result = await fetchAndDetectFileType(
      doc.type === "base64" ? doc.base64 : doc.url,
      doc.fileName
    );

    if (!result.success) {
      this.dispatchEvent(
        new DocumentLoadFailedEvent({ error: "Unable to fetch the document" })
      );
      return;
    }

    const { fileType, blob } = result.data;

    if (fileType === "unknown") {
      this.dispatchEvent(
        new DocumentLoadFailedEvent({ error: "Unsupported file type" })
      );
      return;
    }

    this.container.visible = false;

    this.container.scale = 1;
    this.container.pivot.set(0, 0);
    this.container.x = 0;
    this.container.y = 0;

    // Clear highlights before swapping the sprite so overlays from the previous document are removed.
    for (const h of this.highlightsEntity.highlights) {
      h.graphics.destroy();
    }
    this.highlightsEntity.container.removeChildren();
    this.highlightsEntity.highlights = [];

    if (this.documentEntity.sprite) {
      this.app.stage.removeChild(this.documentEntity.sprite);
      this.documentEntity.sprite.destroy();
    }

    this.document = doc;
    switch (fileType) {
      case "pdf": {
        await this.loadPdf(blob, doc.fileName);
        setCenterPivot(this.container);
        this.fitImageToContainer();
        await this.loadHighResolutionPdf();
        break;
      }
      case "heif":
      case "heic": {
        this.hideHighResolutionPdf();
        this.pdfDocument = undefined;
        const convertedBlob = await heicTo({
          blob,
          type: "image/png",
          quality: 1,
        });
        await this.loadImage(convertedBlob, doc.fileName);

        setCenterPivot(this.container);
        this.fitImageToContainer();
        break;
      }
      default: {
        this.hideHighResolutionPdf();
        this.pdfDocument = undefined;
        await this.loadImage(blob, doc.fileName);

        setCenterPivot(this.container);
        this.fitImageToContainer();
        break;
      }
    }

    this.container.visible = true;
  }

  private async loadImage(blob: Blob, fileName: string): Promise<void> {
    let documentLoadedEvent: DocumentLoadedEvent;
    try {
      const src = URL.createObjectURL(blob);
      const texture = await Assets.load<Texture>({
        src,
        parser: "texture",
      });
      URL.revokeObjectURL(src);
      documentLoadedEvent = new DocumentLoadedEvent({
        documentType: "image",
        fileName,
      });

      // Create sprite from texture
      const sprite = new Sprite(texture);
      sprite.eventMode = "static";
      this.documentEntity.sprite = sprite;

      // Add sprite to stage
      this.documentEntity.container.addChild(this.documentEntity.sprite);
      this.dispatchEvent(documentLoadedEvent);
    } catch (error) {
      console.error("Failed to load image:", error);
    }
  }

  async fitImageToContainer() {
    if (!this.documentEntity.sprite || !this.app.renderer) {
      return;
    }

    const { sprite } = this.documentEntity;
    const canvas = this.app.canvas;

    // Get container dimensions
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    // Get image dimensions
    const imageWidth = sprite.texture.width;
    const imageHeight = sprite.texture.height;

    // Calculate scale to fit image in container while maintaining aspect ratio
    const scaleX = canvasWidth / imageWidth;
    const scaleY = canvasHeight / imageHeight;
    const scale = Math.min(scaleX, scaleY);

    // Apply scale
    this.container.scale.set(scale);

    // Center the image
    this.container.x = canvasWidth / 2;
    this.container.y = canvasHeight / 2;

    // Draw highlights after fitting image
    this.drawHighlights();

    this.dispatchZoomEvent(scale);
    this.drawContainerDebugger();
  }

  // ============================================================================
  // Zoom Operations
  // ============================================================================

  zoomIn(config?: ZoomConfig): number {
    const currentScale = this.getCurrentScale();
    if (!this.documentEntity.sprite || !this.app.renderer) {
      console.warn("Cannot zoom: document sprite or renderer not initialized");
      return currentScale;
    }

    // Calculate new scale
    const increment = 0.2;

    // Find the next multiple of 0.2 greater than current scale
    const newScale = Math.round(currentScale / increment + 1) * increment;
    const clampedScale = this.clampZoom(newScale);

    // If we hit the zoom limits, don't zoom
    if (clampedScale <= currentScale) {
      return currentScale;
    }

    // Get viewport dimensions
    const viewportWidth = this.app.renderer.width;
    const viewportHeight = this.app.renderer.height;

    // Calculate center point of viewport
    const centerX = viewportWidth / 2;
    const centerY = viewportHeight / 2;

    // Convert viewport center to world coordinates
    const worldCenterX = (centerX - this.container.x) / currentScale;
    const worldCenterY = (centerY - this.container.y) / currentScale;

    // Use zoomTo method for smooth animation
    this.zoomTo(
      {
        x: worldCenterX,
        y: worldCenterY,
        zoomLevel: clampedScale,
      },
      config
    );

    return clampedScale;
  }

  zoomOut(config?: ZoomConfig): number {
    const currentScale = this.getCurrentScale();
    if (!this.documentEntity.sprite || !this.app.renderer) {
      console.warn("Cannot zoom: document sprite or renderer not initialized");
      return currentScale;
    }

    // Calculate new scale
    const increment = 0.2;

    // Find the previous multiple of 0.2 less than current scale
    const newScale = Math.round(currentScale / increment - 1) * increment;
    const clampedScale = this.clampZoom(newScale);

    // If we hit the zoom limits, don't zoom
    if (clampedScale >= currentScale) {
      return currentScale;
    }

    // Get viewport dimensions
    const viewportWidth = this.app.renderer.width;
    const viewportHeight = this.app.renderer.height;

    // Calculate center point of viewport
    const centerX = viewportWidth / 2;
    const centerY = viewportHeight / 2;

    // Convert viewport center to world coordinates
    const worldCenterX = (centerX - this.container.x) / currentScale;
    const worldCenterY = (centerY - this.container.y) / currentScale;

    // Use zoomTo method for smooth animation
    this.zoomTo(
      {
        x: worldCenterX,
        y: worldCenterY,
        zoomLevel: clampedScale,
      },
      config
    );

    return clampedScale;
  }

  async zoomTo(
    { x, y, zoomLevel }: ZoomTo,
    config?: ZoomConfig
  ): Promise<void> {
    const documentSprite = this.documentEntity.sprite;

    if (!documentSprite || !this.app.renderer || !documentSprite.parent) {
      console.warn("Cannot zoom: document sprite or renderer not initialized");
      return;
    }

    this.hideHighResolutionPdf();

    // Stop any existing animation
    this.stopZoomAnimation();
    this.clearContainerDebugger();
    this.clearDocumentDebugger();

    // Clamp zoom level to valid range
    const targetZoom = this.clampZoom(zoomLevel);

    // Get renderer dimensions (viewport size)
    const viewportWidth = this.app.renderer.width;
    const viewportHeight = this.app.renderer.height;

    // Calculate the center of the viewport
    const viewportCenterX = viewportWidth / 2;
    const viewportCenterY = viewportHeight / 2;

    const pixelPoint = new PixiPoint(x, y);

    // Calculate target container position to center the coordinate
    const targetContainerX = viewportCenterX - pixelPoint.x * targetZoom;
    const targetContainerY = viewportCenterY - pixelPoint.y * targetZoom;

    const duration = config?.animationDuration ?? 500; // millisecond;
    if (duration === 0) {
      this.container.scale.set(targetZoom);
      this.container.x = targetContainerX;
      this.container.y = targetContainerY;
      this.loadHighResolutionPdf();
      return;
    }

    return new Promise((resolve, reject) => {
      // Store initial values for animation
      const startScale = this.getCurrentScale();
      const startX = this.container.x;
      const startY = this.container.y;

      // Animation parameters
      const startTime = Date.now();

      // Create animation function for ticker
      const animationTick = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeOutCubic(progress);

        // Interpolate values
        const currentScale =
          startScale + (targetZoom - startScale) * easedProgress;
        const currentX = startX + (targetContainerX - startX) * easedProgress;
        const currentY = startY + (targetContainerY - startY) * easedProgress;

        // Apply the interpolated values
        this.container.scale.set(currentScale);
        this.container.x = currentX;
        this.container.y = currentY;

        // Check if animation is complete
        if (progress >= 1) {
          this.stopZoomAnimation();
          this.dispatchZoomEvent(this.getCurrentScale());
          this.drawContainerDebugger();
          this.drawDocumentDebugger();
          this.loadHighResolutionPdf();
          resolve();
        }
      };

      // Store the animation function for cleanup
      this.zoomAnimationTick = animationTick;

      // Add to ticker
      this.app.ticker.add(animationTick);

      setTimeout(() => {
        resolve();
      }, duration + 100);
    });
  }

  /**
   * Fit the viewport to the bounds of a polygon in document pixel space (top-left origin).
   * Coordinates are converted to document-center pivot space before {@link zoomTo}.
   */
  private async zoomToFitDocumentSpacePolygon(
    polygon: readonly Point[]
  ): Promise<void> {
    // TODO: this method is still WIP. It's working for base cases, but doesn't work yet when document is rotated.
    if (
      polygon.length < 3 ||
      !this.documentEntity.sprite ||
      !this.app.renderer
    ) {
      return;
    }

    const boundingBox = new Polygon([...polygon]).getBounds();
    const documentDimension = {
      width: this.documentEntity.container.width,
      height: this.documentEntity.container.height,
    };
    const viewportWidth = this.app.renderer.width;
    const viewportHeight = this.app.renderer.height;
    const padding = 20;
    const zoomLevel = Math.min(
      Math.round(viewportWidth / (boundingBox.width + padding)),
      Math.round(viewportHeight / (boundingBox.height + padding))
    );

    const documentPivotOffset = {
      x: documentDimension.width / 2,
      y: documentDimension.height / 2,
    }; // Calculate offset since the document's pivot is at the center of the document
    const centerX =
      boundingBox.x + boundingBox.width / 2 - documentPivotOffset.x;
    const centerY =
      boundingBox.y + boundingBox.height / 2 - documentPivotOffset.y;

    await this.zoomTo({ x: centerX, y: centerY, zoomLevel });
  }

  async zoomToHighlight(id: string): Promise<void> {
    this.hideHighlightNote();
    const highlightWithGraphics = this.highlightsEntity.highlights.find(
      (h) => h.highlight.id === id
    );
    if (!highlightWithGraphics) {
      console.warn("Cannot zoom to highlight: invalid id " + id);
      return;
    }

    const { highlight, graphics } = highlightWithGraphics;

    await this.zoomToFitDocumentSpacePolygon(highlight.polygon);
    const bound = graphics.getBounds();
    this.showHighlightNote(highlight, {
      x: bound.minX,
      y: bound.maxY,
    });
  }

  async zoomToPolygon(polygon: readonly Point[]): Promise<void> {
    this.hideHighlightNote();
    await this.zoomToFitDocumentSpacePolygon(polygon);
  }

  getCurrentScale(): number {
    return this.container.scale.x; // ASSUMPTION: scale.x always equals to scale.y
  }

  clampZoom(zoomLevel: number): number {
    return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoomLevel));
  }

  private stopZoomAnimation() {
    if (this.zoomAnimationTick) {
      this.app.ticker.remove(this.zoomAnimationTick);
      this.zoomAnimationTick = undefined;
    }
  }

  private dispatchZoomEvent(scale: number): void {
    this.dispatchEvent(new ZoomChangedEvent(scale));
  }

  // ============================================================================
  // Rotation Operations
  // ============================================================================

  rotateRight(): void {
    if (!this.documentEntity.sprite || !this.app.renderer) {
      return;
    }

    this.container.rotation += Math.PI / 4;
    this.syncAndShowHighResolutionPdf();
    this.drawContainerDebugger();
  }

  rotateLeft(): void {
    if (!this.documentEntity.sprite || !this.app.renderer) {
      return;
    }

    this.container.rotation -= Math.PI / 4;
    this.syncAndShowHighResolutionPdf();
    this.drawContainerDebugger();
  }

  // ============================================================================
  // Focus
  // ============================================================================
  focus(): void {
    if (!this.app.canvas) {
      return;
    }

    this.app.canvas.focus();
  }

  // ============================================================================
  // Pan Mode Management
  // ============================================================================

  private initPanMode() {
    const canvas = this.app.renderer?.canvas;
    if (!canvas) {
      return;
    }

    canvas.style.cursor = "grab";
    canvas.tabIndex = 0;

    // Mouse and pointer events
    canvas.addEventListener("keydown", this.onKeyDown);
    canvas.addEventListener("keyup", this.onKeyUp);
    canvas.addEventListener("pointerdown", this.onDragStart);
    canvas.addEventListener("pointermove", this.onDragging);
    canvas.addEventListener("pointerup", this.onDragEnd);
    canvas.addEventListener("pointerleave", this.onDragEnd);
    canvas.addEventListener("wheel", this.onMouseWheel, {
      passive: false,
    });

    // Touch events for pinch gestures
    canvas.addEventListener("touchstart", this.onTouchStart, {
      passive: false,
    });
    canvas.addEventListener("touchmove", this.onTouchMove, { passive: false });
    canvas.addEventListener("touchend", this.onTouchEnd, { passive: false });
    canvas.addEventListener("touchcancel", this.onTouchEnd, { passive: false });
  }

  private destroyPanMode() {
    const canvas = this.app.renderer?.canvas;
    if (!canvas) {
      return;
    }

    // Remove mouse and pointer events
    canvas.removeEventListener("keydown", this.onKeyDown);
    canvas.removeEventListener("keyup", this.onKeyUp);
    canvas.removeEventListener("pointerdown", this.onDragStart);
    canvas.removeEventListener("pointermove", this.onDragging);
    canvas.removeEventListener("pointerup", this.onDragEnd);
    canvas.removeEventListener("pointerleave", this.onDragEnd);
    canvas.removeEventListener("wheel", this.onMouseWheel);

    // Remove touch events
    canvas.removeEventListener("touchstart", this.onTouchStart);
    canvas.removeEventListener("touchmove", this.onTouchMove);
    canvas.removeEventListener("touchend", this.onTouchEnd);
    canvas.removeEventListener("touchcancel", this.onTouchEnd);
  }

  // ============================================================================
  // Mouse & Touch Event Handlers
  // ============================================================================

  private onKeyDown = (event: KeyboardEvent) => {
    if (this.mode.mode !== "pan") {
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      switch (event.key) {
        case "+":
        case "=":
          event.preventDefault();
          this.zoomIn();
          return;
        case "-":
          event.preventDefault();
          this.zoomOut();
          return;
      }
    }

    let deltaX = 0;
    let deltaY = 0;

    switch (event.key) {
      case "ArrowUp":
        deltaY = KEYBOARD_PAN_STEP;
        break;
      case "ArrowDown":
        deltaY = -KEYBOARD_PAN_STEP;
        break;
      case "ArrowLeft":
        deltaX = KEYBOARD_PAN_STEP;
        break;
      case "ArrowRight":
        deltaX = -KEYBOARD_PAN_STEP;
        break;
      default:
        return;
    }

    event.preventDefault();
    window.clearTimeout(this.panDebounceTimer);
    this.hideHighResolutionPdf();
    this.hideHighlightNote();

    this.container.x += deltaX;
    this.container.y += deltaY;

    this.drawContainerDebugger();
    this.drawDocumentDebugger();
  };

  private onKeyUp = (event: KeyboardEvent) => {
    if (this.mode.mode !== "pan") {
      return;
    }

    if ((event.ctrlKey || event.metaKey) && event.key === "r") {
      event.preventDefault();
      this.rotateRight();
      return;
    }

    if ((event.ctrlKey || event.metaKey) && event.key === "R") {
      event.preventDefault();
      this.rotateLeft();
      return;
    }

    switch (event.key) {
      case "ArrowUp":
      case "ArrowDown":
      case "ArrowLeft":
      case "ArrowRight":
        window.clearTimeout(this.panDebounceTimer);
        this.panDebounceTimer = window.setTimeout(() => {
          this.syncAndShowHighResolutionPdf();
        }, 100);
        break;
    }
  };

  private onMouseWheel = async (event: WheelEvent) => {
    if (!this.documentEntity.sprite || !this.app.renderer) {
      return;
    }

    event.preventDefault();

    if (!event.ctrlKey && !event.metaKey) {
      // two finger on touchpad => pan
      this.container.x -= event.deltaX;
      this.container.y -= event.deltaY;

      event.stopPropagation();
      this.drawContainerDebugger();
      this.drawDocumentDebugger();
      this.syncAndShowHighResolutionPdf();
      this.dispatchEvent(
        new AmplitudeTrackEvent("File panned", { source: "wheel" })
      );
    } else {
      // mouse wheel + ctrl/cmd key OR touchpad two finger pinch
      const rect = this.app.canvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      const normalizedDeltaY =
        event.deltaMode === WheelEvent.DOM_DELTA_LINE
          ? event.deltaY * 16
          : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
          ? event.deltaY * rect.height
          : event.deltaY;

      // NOTE: there's no reliable way to differentiate pinch to zoom with mouse wheel + ctrl event.
      // mouse wheel usually have much larger deltaY compared to pinch, so we pick arbitrary number based on experiment.
      const isPinchZoom = Math.abs(event.deltaY) < 10;

      // Adjust intensity for pinch to zoom to get more natural feels vs using mouse wheel.
      const zoomIntensity = isPinchZoom ? 0.008 : 0.001;
      const zoomFactor = Math.exp(-normalizedDeltaY * zoomIntensity);
      const currentScale = this.getCurrentScale();
      const newScale = this.clampZoom(currentScale * zoomFactor);

      if (newScale === currentScale) {
        return;
      }

      const worldX = mouseX - this.container.x;
      const worldY = mouseY - this.container.y;
      const actualZoomFactor = newScale / currentScale;

      this.container.scale.set(newScale);
      this.container.x = mouseX - worldX * actualZoomFactor;
      this.container.y = mouseY - worldY * actualZoomFactor;

      event.stopPropagation();
      await this.loadHighResolutionPdf();
      this.dispatchZoomEvent(newScale);
      this.drawContainerDebugger();
      this.drawDocumentDebugger();
      this.dispatchEvent(
        new AmplitudeTrackEvent(
          newScale > currentScale ? "File zoomed in" : "File zoomed out",
          {
            source: isPinchZoom ? "touchpad" : "wheel",
          }
        )
      );
    }
  };

  private onDragStart = (event: PointerEvent) => {
    if (this.mode.mode !== "pan" || this.mode.isPinching) {
      return;
    }

    // Ignore touch pointer events since we handle them separately
    if (event.pointerType === "touch") {
      return;
    }

    this.hideHighResolutionPdf();
    this.mode.isDragging = true;
    this.mode.lastPointerX = event.x;
    this.mode.lastPointerY = event.y;

    this.app.canvas.style.cursor = "grabbing";
    event.stopPropagation();
    this.dispatchEvent(
      new AmplitudeTrackEvent("File panned", { source: "pointer" })
    );
  };

  private onDragging = (event: PointerEvent) => {
    if (
      this.mode.mode !== "pan" ||
      !this.mode.isDragging ||
      this.mode.isPinching
    ) {
      return;
    }

    // Ignore touch pointer events since we handle them separately
    if (event.pointerType === "touch") {
      return;
    }

    this.hideHighlightNote();

    // Calculate the difference in position
    const deltaX = event.x - this.mode.lastPointerX;
    const deltaY = event.y - this.mode.lastPointerY;

    // Apply the movement to the container
    this.container.x += deltaX;
    this.container.y += deltaY;

    // Update drag data for next movement
    this.mode.lastPointerX = event.x;
    this.mode.lastPointerY = event.y;

    event.stopPropagation();
    this.drawContainerDebugger();
    this.drawDocumentDebugger();
  };

  private onDragEnd = (event: PointerEvent) => {
    if (
      this.mode.mode !== "pan" ||
      !this.mode.isDragging ||
      this.mode.isPinching
    ) {
      return;
    }

    // Ignore touch pointer events since we handle them separately
    if (event.pointerType === "touch") {
      return;
    }

    this.mode.isDragging = false;
    this.app.canvas.style.cursor = "grab";
    event.stopPropagation();

    this.syncAndShowHighResolutionPdf();
    this.drawContainerDebugger();
    this.drawDocumentDebugger();
  };

  private onTouchStart = (event: TouchEvent) => {
    if (this.mode.mode !== "pan") {
      return;
    }

    event.preventDefault();

    if (event.touches.length === 2) {
      // Start pinch gesture
      this.mode.isPinching = true;
      this.mode.isDragging = false;

      const touch1 = event.touches[0];
      const touch2 = event.touches[1];

      if (!touch1 || !touch2) {
        return;
      }

      this.hideHighResolutionPdf();

      // Calculate initial distance between touches
      this.mode.initialPinchDistance = this.getTouchDistance(touch1, touch2);
      this.mode.initialScale = this.getCurrentScale();

      // Calculate center point between touches
      const rect = this.app.canvas.getBoundingClientRect();
      this.mode.pinchCenterX =
        (touch1.clientX + touch2.clientX) / 2 - rect.left;
      this.mode.pinchCenterY = (touch1.clientY + touch2.clientY) / 2 - rect.top;

      this.dispatchEvent(new HighlightNoteEvent({ action: "hide" }));
    } else if (event.touches.length === 1 && !this.mode.isPinching) {
      this.hideHighResolutionPdf();
      // Single touch - start panning
      const touch = event.touches[0];
      if (!touch) {
        return;
      }
      this.mode.isDragging = true;
      this.mode.lastPointerX = touch.clientX;
      this.mode.lastPointerY = touch.clientY;
      this.app.canvas.style.cursor = "grabbing";

      this.dispatchEvent(new HighlightNoteEvent({ action: "hide" }));
      this.dispatchEvent(
        new AmplitudeTrackEvent("File panned", { source: "touch" })
      );
    }
  };

  private onTouchMove = async (event: TouchEvent) => {
    if (this.mode.mode !== "pan") {
      return;
    }

    event.preventDefault();

    if (event.touches.length === 2 && this.mode.isPinching) {
      // Handle pinch gesture
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];

      if (!touch1 || !touch2) {
        return;
      }

      const currentDistance = this.getTouchDistance(touch1, touch2);
      const scale =
        (currentDistance / this.mode.initialPinchDistance) *
        this.mode.initialScale;

      // Apply zoom limits
      const newScale = this.clampZoom(scale);
      const currentScale = this.getCurrentScale();

      if (newScale !== currentScale) {
        // Calculate the world position of the pinch center
        const worldX = this.mode.pinchCenterX - this.container.x;
        const worldY = this.mode.pinchCenterY - this.container.y;

        // Calculate the scaling factor actually applied
        const actualZoomFactor = newScale / this.getCurrentScale();

        // Apply the new scale
        this.container.scale.set(newScale);

        // Adjust container position to zoom towards pinch center
        this.container.x = this.mode.pinchCenterX - worldX * actualZoomFactor;
        this.container.y = this.mode.pinchCenterY - worldY * actualZoomFactor;
        this.dispatchZoomEvent(newScale);

        this.dispatchEvent(
          new AmplitudeTrackEvent(
            newScale > currentScale ? "File zoomed in" : "File zoomed out",
            {
              source: "touch",
            }
          )
        );
      }
    } else if (
      event.touches.length === 1 &&
      this.mode.isDragging &&
      !this.mode.isPinching
    ) {
      // Handle single touch pan
      const touch = event.touches[0];
      if (!touch) {
        return;
      }
      const deltaX = touch.clientX - this.mode.lastPointerX;
      const deltaY = touch.clientY - this.mode.lastPointerY;

      this.container.x += deltaX;
      this.container.y += deltaY;

      this.mode.lastPointerX = touch.clientX;
      this.mode.lastPointerY = touch.clientY;
    }

    this.drawContainerDebugger();
    this.drawDocumentDebugger();
  };

  private onTouchEnd = async (event: TouchEvent) => {
    if (this.mode.mode !== "pan") {
      return;
    }

    event.preventDefault();

    if (event.touches.length < 2) {
      // End pinch gesture
      this.mode.isPinching = false;
    }

    if (event.touches.length === 0) {
      // All touches ended
      this.mode.isDragging = false;
      this.mode.isPinching = false;
      this.app.canvas.style.cursor = "grab";
      await this.loadHighResolutionPdf();
    }
  };

  private getTouchDistance(touch1: Touch, touch2: Touch): number {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // ============================================================================
  // Highlight Management
  // ============================================================================

  /**
   * Set new highlights and redraw them
   * @param highlights Array of highlight objects to display
   */
  public setHighlights(highlights: readonly Highlight[]) {
    // TODO: optimize + prevent memory leak this by reusing graphics.
    this.highlightsEntity.highlights = highlights.map((highlight) => ({
      highlight,
      graphics: new Graphics(),
    }));
    this.drawHighlights();
  }

  /**
   * Clear all highlights from the canvas
   */
  clearHighlights() {
    this.highlightsEntity.highlights = [];
    this.drawHighlights();
  }

  private drawHighlights() {
    const documentSprite = this.documentEntity.sprite;
    if (!documentSprite) {
      return;
    }

    this.highlightsEntity.container.removeChildren();
    if (this.highlightsEntity.highlights.length === 0) {
      return;
    }

    const { x, y, width, height } = documentSprite;
    let overlay = new Graphics();
    overlay = overlay.rect(x, y, width, height).fill({
      color: "black",
      alpha: 0.25,
    });

    for (const h of this.highlightsEntity.highlights) {
      overlay = overlay.poly(h.highlight.polygon, true).cut();
      h.graphics
        .poly(h.highlight.polygon, true)
        .fill({ color: "white", alpha: 0 });
      h.graphics.eventMode = "static";
      h.graphics.on("mouseover", (event: MouseEvent) => {
        const { x, y } = event;
        const viewport = this.app.renderer.canvas.getBoundingClientRect();
        if (
          x < viewport.left ||
          y < viewport.top ||
          x > viewport.right ||
          y > viewport.bottom
        ) {
          // outside of viewport.. ignore this event;
          return;
        }

        const bound = h.graphics.getBounds();
        this.showHighlightNote(h.highlight, {
          x: bound.minX,
          y: bound.maxY,
        });
      });

      h.graphics.on("mouseout", () => {
        this.hideHighlightNote();
      });
    }

    overlay.eventMode = "static";
    this.highlightsEntity.container.removeChildren();
    this.highlightsEntity.container.addChild<Graphics[]>(overlay);
    this.highlightsEntity.container.addChild<Graphics[]>(
      ...this.highlightsEntity.highlights.map((h) => h.graphics)
    );
  }

  private showHighlightNote(highlight: Highlight, point: Point) {
    this.dispatchEvent(
      new HighlightNoteEvent({
        action: "show",
        highlight,
        x: Math.max(point.x, 0),
        y: Math.max(point.y, 0),
      })
    );
  }

  private hideHighlightNote() {
    this.dispatchEvent(new HighlightNoteEvent({ action: "hide" }));
  }

  // ============================================================================
  // PDF
  // ============================================================================
  private pdfDocument:
    | {
        document: PDFDocumentProxy;
        currentPage: number;
        originalWidth: number;
        originalHeight: number;
      }
    | undefined;

  private highResolutionContainer: Container;
  private highResolutionSprite: Sprite;
  private hideHighResolutionPdf() {
    if (!this.pdfDocument) {
      return;
    }

    this.highResolutionContainer.visible = false;
  }

  private syncAndShowHighResolutionPdf() {
    if (!this.pdfDocument) {
      return;
    }

    // Reset the pivot to center of the high resolution continaer
    this.highResolutionContainer.pivot.set(
      this.highResolutionContainer.width / 2,
      this.highResolutionContainer.height / 2
    );
    this.highResolutionContainer.x = this.highResolutionContainer.width / 2;
    this.highResolutionContainer.y = this.highResolutionContainer.height / 2;

    // Ensure that high resolution container is visible
    this.highResolutionContainer.visible = true;

    // Sync high resolution's coordinate and rotation with low resolution's coordinate and rotation
    this.highResolutionContainer.x = this.container.x;
    this.highResolutionContainer.y = this.container.y;
    this.highResolutionContainer.rotation = this.container.rotation;
  }

  private async loadHighResolutionPdf() {
    if (!this.pdfDocument) {
      return;
    }

    this.hideHighResolutionPdf();

    const scaledWidth = this.container.width;
    const pdfScale = scaledWidth / this.pdfDocument.originalWidth;

    const highResolutionTexture = await renderPdfPage(
      this.pdfDocument.document,
      this.pdfDocument.currentPage,
      pdfScale
    );
    this.highResolutionSprite.texture = highResolutionTexture;
    this.drawHighResolutionPdfDebugger();
    this.syncAndShowHighResolutionPdf();
  }

  async loadPdf(blob: Blob, fileName: string) {
    try {
      const pdfDocument = await loadPDF(blob);
      const currentPage = 1;
      const texture = await renderPdfPage(pdfDocument, currentPage, 1);

      // Create sprite from texture
      const sprite = new Sprite(texture);
      sprite.eventMode = "static";
      this.documentEntity.sprite = sprite;

      // Add sprite to stage
      this.documentEntity.container.addChild(this.documentEntity.sprite);
      this.dispatchEvent(
        new DocumentLoadedEvent({
          documentType: "pdf",
          numberOfPages: pdfDocument.numPages,
          fileName,
        })
      );

      this.pdfDocument = {
        document: pdfDocument,
        currentPage,
        originalHeight: texture.height,
        originalWidth: texture.width,
      };
    } catch (error) {
      console.error("Failed to load pdf:", error);
    }
  }

  destroyPdf() {
    if (!this.pdfDocument) {
      return;
    }
    this.pdfDocument.document.destroy();
    this.pdfDocument = undefined;
  }

  async nextPage(): Promise<{ pageNumber: number }> {
    const pdfDocument = this.pdfDocument;
    if (!pdfDocument || !this.documentEntity.sprite) {
      console.warn(
        "Calling nextPage when pdfDocument or sprite is not available"
      );
      return { pageNumber: 0 };
    }

    try {
      const nextPageNumber = Math.min(
        pdfDocument.document.numPages,
        pdfDocument.currentPage + 1
      );
      if (nextPageNumber === pdfDocument.currentPage) {
        return { pageNumber: pdfDocument.currentPage };
      }

      const texture = await renderPdfPage(
        pdfDocument.document,
        nextPageNumber,
        1
      );
      this.documentEntity.sprite.texture = texture;
      pdfDocument.currentPage = nextPageNumber;

      await this.loadHighResolutionPdf();

      return { pageNumber: nextPageNumber };
    } catch (error) {
      console.error("Error calling nextPage", error);
      return { pageNumber: pdfDocument.currentPage };
    }
  }

  async previousPage(): Promise<{ pageNumber: number }> {
    const pdfDocument = this.pdfDocument;
    if (!pdfDocument || !this.documentEntity.sprite) {
      console.warn(
        "Calling previousPage when pdfDocument or sprite is not available"
      );
      return { pageNumber: 0 };
    }

    try {
      const previousPageNumber = Math.max(1, pdfDocument.currentPage - 1);
      if (previousPageNumber === pdfDocument.currentPage) {
        return { pageNumber: pdfDocument.currentPage };
      }

      const texture = await renderPdfPage(
        pdfDocument.document,
        previousPageNumber,
        1
      );
      this.documentEntity.sprite.texture = texture;
      pdfDocument.currentPage = previousPageNumber;
      this.loadHighResolutionPdf();
      return { pageNumber: previousPageNumber };
    } catch (error) {
      console.error("Error calling previousPage", error);
      return { pageNumber: pdfDocument.currentPage };
    }
  }

  // ============================================================================
  // Debug Utilities
  // ============================================================================

  setDebugger(key: keyof Debugger, enable: boolean): void {
    this.debugger[key] = enable;
    this.drawDebuggers();
  }

  private initDebugger() {
    this.app.stage.addChild(this.debugger._containers.document);
    this.app.stage.addChild(this.debugger._containers.canvas);
    this.app.stage.addChild(this.debugger._containers.container);
    this.drawDebuggers();
  }

  private destroyDebugger() {
    this.debugger._containers.canvas.destroy();
    this.debugger._containers.container.destroy();
    this.debugger._containers.document.destroy();
  }

  private drawDebuggers() {
    this.drawDocumentDebugger();
    this.drawCanvasDebugger();
    this.drawContainerDebugger();
    this.drawHighResolutionPdfDebugger();
  }

  private drawHighResolutionPdfDebugger() {
    if (!this.pdfDocument) {
      return;
    }

    if (this.debugger.highResolutionPdf) {
      this.debugger._highResolutionPdf.clear();
      this.debugger._highResolutionPdf
        .rect(
          0,
          0,
          this.highResolutionSprite.width,
          this.highResolutionSprite.height
        )
        .stroke({ width: 2, color: "blue" });
    }
  }

  private drawContainerDebugger() {
    this.debugger._containers.container.removeChildren();
    if (this.debugger.container) {
      const bound = this.container.getBounds();
      const rect = {
        x: bound.minX,
        y: bound.minY,
        width: bound.width,
        height: bound.height,
      };
      const grid = drawGridRectangle(
        rect,
        {
          ...rect,
          x: 0,
          y: 0,
        },
        {
          gridSize: 50,
          color: "purple",
        }
      );
      this.debugger._containers.container.addChild(grid);

      const pivotPosition = this.container.toGlobal(this.container.pivot);
      const pivot = new Graphics()
        .circle(pivotPosition.x, pivotPosition.y, 10)
        .fill("purple");
      this.debugger._containers.container.addChild(pivot);
    }
  }

  private drawDocumentDebugger() {
    this.debugger._containers.document.removeChildren();
    if (this.debugger.document && this.documentEntity.sprite) {
      const { x, y } = this.documentEntity.sprite.getGlobalPosition();
      const { width, height } = this.documentEntity.sprite;
      const scale = this.getCurrentScale();
      const grid = drawGridRectangle(
        { x, y, width: width * scale, height: height * scale },
        this.documentEntity.sprite,
        {
          gridSize: 50,
          color: "red",
        }
      );
      this.debugger._containers.document.addChild(grid);
    }
  }

  private drawCanvasDebugger() {
    this.debugger._containers.canvas.removeChildren();
    if (this.debugger.canvas) {
      const grid = drawGridRectangle(
        {
          x: 0,
          y: 0,
          width: this.app.canvas.width,
          height: this.app.canvas.height,
        },
        {
          x: 0,
          y: 0,
          width: this.app.canvas.width,
          height: this.app.canvas.height,
        },
        {
          gridSize: 50,
          color: "blue",
        }
      );
      this.debugger._containers.canvas.addChild(grid);
    }
  }

  private clearContainerDebugger() {
    if (this.debugger.container) {
      this.debugger._containers.container.removeChildren();
    }
  }

  private clearDocumentDebugger() {
    if (this.debugger.document) {
      this.debugger._containers.document.removeChildren();
    }
  }

  private dispatchDebugEvent(data: DebugMessageDetail) {
    this.dispatchEvent(new DebugMessageEvent(data));
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

// Function to draw a rectangle with grid and coordinates
function drawGridRectangle(
  rectangle: {
    x: number;
    y: number;
    width: number;
    height: number;
  },
  label: {
    x: number;
    y: number;
    width: number;
    height: number;
  },
  config: {
    gridSize: number;
    color: string;
  }
) {
  const container = new Container();
  const graphics = new Graphics();
  const { x, y, width, height } = rectangle;
  const { gridSize, color } = config;

  // Draw the main rectangle border
  graphics.setStrokeStyle({ width: 2, color });
  graphics.rect(x, y, width, height).stroke();
  const scale = rectangle.width / label.width;

  // Draw vertical grid lines and add x coordinates
  graphics.setStrokeStyle({ width: 1, color }); // Gray grid lines, 1px thick
  for (let i = 0; i <= label.width; i += gridSize) {
    const lineX = x + i * scale;

    // Draw vertical line
    graphics.moveTo(lineX, y);
    graphics.lineTo(lineX, y + height).stroke();

    const labelX = label.x + i;

    if (i > 0) {
      // // Add x coordinate label at the top of each grid line
      const xLabel = new Text({
        text: `${Math.round(labelX)}`,
        style: {
          fontFamily: "Arial",
          fontSize: 10,
          fill: 0x000000,
          align: "center",
        },
      });
      xLabel.anchor.set(0, 0);
      xLabel.x = lineX;
      xLabel.y = y;

      container.addChild(xLabel);
    }
  }

  // Draw horizontal grid lines and add y coordinates
  for (let i = 0; i <= label.height; i += gridSize) {
    const lineY = y + i * scale;

    // Draw horizontal line
    graphics.moveTo(x, lineY);
    graphics.lineTo(x + width, lineY).stroke();

    const labelY = label.y + i;

    if (i > 0) {
      // Add y coordinate label at the left of each grid line
      const yLabel = new Text({
        text: `${Math.round(labelY)}`,
        style: {
          fontFamily: "Arial",
          fontSize: 10,
          fill: 0x000000,
          align: "right",
        },
      });
      yLabel.anchor.set(0, 0);
      yLabel.x = x;
      yLabel.y = lineY;

      container.addChild(yLabel);
    }
  }

  // Add first (x, y) coordinate label
  const firstLabel = new Text({
    text: `(${Math.round(label.x)},${Math.round(label.y)})`,
    style: {
      fontFamily: "Arial",
      fontSize: 10,
      fill: 0x000000,
      align: "right",
    },
  });
  firstLabel.anchor.set(0, 0);
  firstLabel.x = x;
  firstLabel.y = y;

  container.addChild(firstLabel);

  // // Add the graphics to the stage
  // app.stage.addChild(graphics);

  container.addChild(graphics);
  return container;
}

function setCenterPivot(container: Container) {
  // reset pivot to center of document
  container.pivot.set(container.width / 2, container.height / 2);
  container.x = container.width / 2;
  container.y = container.height / 2;
}

// ============================================================================
// Type Declarations
// ============================================================================

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      // Declare `<document-viewer/>` as a valid JSX element
      "document-viewer": {
        ref: React.MutableRefObject<DocumentViewerElement | undefined>;
      };
    }
  }
}
